import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "../../../../context/CanvasContext";
import ConfirmationModal from "../../../../../shared/components/modals/ConfirmationModal";
import EditModal from "../../../../../shared/components/modals/EditModal";
import { FiPlus, FiArrowLeft, FiPieChart, FiTrendingUp, FiTrendingDown, FiEdit2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { FiTrash2 } from "react-icons/fi";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import {
  fetchInvestments,
  createInvestment,
  updateInvestment,
  deleteInvestment,
  uploadInvestments,
  selectInvestments,
  selectInvestmentLoading,
  selectInvestmentPagination,
  investmentOverview,
  selectInvestmentOverview,
  addTransactionToAsset,
  updateTransaction,
  updateAsset,
  deleteAssetWithTransactions
} from "../../../../../../app/store/slices/investmentSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import TableHeader from "../../../../../shared/components/TableHeader";
import TableBody from '../../../../../shared/components/TableBody';
import FileUploadButton from "../../../../../shared/components/FileUploadButton";
import ActionButtons from "../../../../../shared/components/ActionButton";
import FormInput from "../../../../../shared/ui/FormInput";
import FormSelect from "../../../../../shared/ui/FormSelect";
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import { investmentTypes, tradeOptions } from "../../../../../../utils/constants/dashboardConstants";
import InstructionsModal from "../../../../../shared/components/InstructionsModal";
import SummaryCard from "../../../../../shared/components/SummaryCard";
import { MdShowChart } from "react-icons/md";
import { FaCoins } from "react-icons/fa";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import { currencyOptions} from '../../../../../../utils/constants/currencyOptions';
import {formatAmount} from '../../../../../../utils/formatAmount';
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import Pagination from "../../../../../shared/components/Pagination";
import {exchangeToRegion} from '../../../../../../utils/constants/exchange';
import {portfolioColumns, transactionColumns} from '../../../../../../utils/constants/dashboardConstants'
import { formatCurrency, formatPercentage, formatUnits} from '../../../../../../utils';


const InvestmentContent = () => {
  const dispatch = useDispatch(); 
  const { selectedCanvas } = useCanvas();
  const investments = useSelector(selectInvestments);
  const loading = useSelector(selectInvestmentLoading);
  const pagination = useSelector(selectInvestmentPagination);
  const accounts = useSelector(selectAccounts);
  const overview = useSelector(selectInvestmentOverview);

  // View management states
  const [currentView, setCurrentView] = useState('portfolio');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetTransactions, setAssetTransactions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(10);

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAssetConfirm, setShowDeleteAssetConfirm] = useState(false); 
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddTransactionForm, setShowAddTransactionForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);

  // Data states
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [addInvestmentData, setAddInvestmentData] = useState({
    assetName: "",
    ticker: "",
    type: "",
    trade: "",
    units: "",
    cost: "",
    orderPrice: "",
    paymentAccountId: "",
    currency: "",
    date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  
  const [addTransactionData, setAddTransactionData] = useState({
    trade: "",
    units: "",
    cost: "",
    orderPrice: "",
    paymentAccountId: "",
    currency: "",
    date: new Date().toISOString().split('T')[0],
    notes: "",
  });
  const [editTransactionData, setEditTransactionData] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editAssetData, setEditAssetData] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); 
  const fileInputRef = useRef();
  const addOptionsRef = useRef(null);
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount, currencyCode, assetCurrency = null) => {
    const code = assetCurrency || currencyCode || selectedCanvas?.currency || "USD";
    return `${code} ${Number(amount || 0).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };
  
  const portfolioAssets = React.useMemo(() => {
    if (!Array.isArray(investments) || investments.length === 0) return [];

    const assetMap = new Map();
    investments.forEach(transaction => {
      if (!transaction || !transaction.assetName || !transaction.ticker) return;
      const key = `${transaction.assetName}-${transaction.ticker}`;
      if (assetMap.has(key)) {
        const existing = assetMap.get(key);
        const isBuy = transaction.trade?.toLowerCase() === 'buy';
        existing.totalUnits += isBuy ? Number(transaction.units || 0) : -Number(transaction.units || 0);
        existing.totalCost += isBuy ? Number(transaction.cost || 0) : -Number(transaction.cost || 0);
        existing.transactions.push(transaction);
      } else {
        const isBuy = transaction.trade?.toLowerCase() === 'buy';
        assetMap.set(key, {
          assetName: transaction.assetName,
          ticker: transaction.ticker,
          type: transaction.type,
          currency: transaction.currency,
          exchange: transaction.exchange,
          totalUnits: isBuy ? Number(transaction.units || 0) : -Number(transaction.units || 0),
          totalCost: isBuy ? Number(transaction.cost || 0) : -Number(transaction.cost || 0),
          transactions: [transaction]
        });
      }
    });

    // Calculate derived values
    const assets = Array.from(assetMap.values()).filter(asset => asset.totalUnits > 0);
    let totalPortfolioValue = 0;
    
    assets.forEach(asset => {
      let lastClosePrice = asset.lastClosePrice;
      let lastClosePriceDate = asset?.lastClosePriceDate || asset?.createdAt;
      if ((!lastClosePrice || !lastClosePriceDate) && Array.isArray(asset.transactions) && asset.transactions.length > 0) {
        const sortedTx = [...asset.transactions].sort((a, b) => new Date(b.lastClosePriceDate || b.createdAt || 0) - new Date(a.lastClosePriceDate || a.createdAt || 0));
        const txWithPrice = sortedTx.find(tx => tx.lastClosePrice !== undefined);
        if (txWithPrice) {
          if (!lastClosePrice && txWithPrice.lastClosePrice !== undefined) lastClosePrice = txWithPrice.lastClosePrice;
          if (!lastClosePriceDate && txWithPrice.lastClosePriceDate) lastClosePriceDate = txWithPrice.lastClosePriceDate;
        }
      }
      asset.lastClosePrice = lastClosePrice;
      asset.lastClosePriceDate = lastClosePriceDate;
      const weightedAverageOrderPrice = asset.totalUnits > 0 
        ? asset.totalCost / asset.totalUnits 
        : 0;
      asset.averageOrderPrice = Number(weightedAverageOrderPrice.toFixed(4));
      asset.currentPrice = lastClosePrice;
      asset.currentValue = asset.totalUnits * lastClosePrice;
      totalPortfolioValue += asset.currentValue;
    });

    return assets.map(asset => ({
      ...asset,
      portfolioPercentage: totalPortfolioValue > 0 ? (asset.currentValue / totalPortfolioValue) * 100 : 0,
      profitLoss: asset.currentValue - asset.totalCost,
      profitLossPercentage: asset.totalCost > 0 ? ((asset.currentValue - asset.totalCost) / asset.totalCost) * 100 : 0
    })).sort((a, b) => b.currentValue - a.currentValue);
  }, [investments]);

  const paginatedPortfolioAssets = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return portfolioAssets.slice(start, end);
  }, [portfolioAssets, currentPage, pageSize]);

  const paginatedTransactions = React.useMemo(() => {
    const start = (transactionPage - 1) * transactionPageSize;
    const end = start + transactionPageSize;
    return assetTransactions.slice(start, end);
  }, [assetTransactions, transactionPage, transactionPageSize]);

  const portfolioPagination = React.useMemo(() => ({
    total: portfolioAssets.length,
    page: currentPage,
    pages: Math.ceil(portfolioAssets.length / pageSize)
  }), [portfolioAssets.length, currentPage, pageSize]);

  const transactionPagination = React.useMemo(() => ({
    total: assetTransactions.length,
    page: transactionPage,
    pages: Math.ceil(assetTransactions.length / transactionPageSize)
  }), [assetTransactions.length, transactionPage, transactionPageSize]);

  
  const handleAssetClick = (asset) => {
    setSelectedAsset(asset);
    setAssetTransactions(asset.transactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    setCurrentView('transactions');
    setTransactionPage(1);
  };

  const handleBackToPortfolio = () => {
    setCurrentView('portfolio');
    setSelectedAsset(null);
    setAssetTransactions([]);
    setEditingTransaction(null);
    setTransactionPage(1);
  };

  // Handle Add Investment 
  const handleAddInvestment = () => {
    setAddInvestmentData({
      assetName: "",
      ticker: "",
      type: "",
      trade: "",
      units: "",
      cost: "",
      orderPrice: "",
      paymentAccountId: "",
      currency: "",
      date: new Date().toISOString().split('T')[0],
      notes: "",
    });
    setShowAddForm(true);
    setShowAddOptions(false);
  };

  // Handle Add Transaction
  const handleAddTransaction = () => {
    setAddTransactionData({
      trade: "",
      units: "",
      cost: "",
      orderPrice: "",
      paymentAccountId: "",
      currency: "",
      date: new Date().toISOString().split('T')[0],
      notes: "",
    });
    setShowAddTransactionForm(true);
  };

  // Create new investment
  const handleCreateInvestment = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error("Invalid Canvas");
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createInvestment({
          canvasId: selectedCanvas.id,
          investmentData: {
            ...addInvestmentData,
            units: Number(Number(addInvestmentData.units)),
            cost: Number(Number(addInvestmentData.cost)),
            orderPrice: Number(Number(addInvestmentData.orderPrice)),
          },
        })
      ).unwrap();
      
      setShowAddForm(false);
      setAddInvestmentData({
        assetName: "",
        ticker: "",
        type: "",
        trade: "",
        units: "",
        cost: "",
        orderPrice: "",
        paymentAccountId: "",
        currency: "",
        date: new Date().toISOString().split('T')[0],
        notes: "",
      });
      
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
      toast.success("Investment Created Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  // Create new transaction 
  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id || !selectedAsset) {
      toast.error("Invalid Canvas or Asset");
      return;
    }
    setIsAdding(true);
    try {
      const transactionPayload = {
        ...addTransactionData,
        assetName: selectedAsset.assetName,
        ticker: selectedAsset.ticker,
        type: selectedAsset.type,
        units: Number(Number(addTransactionData.units)),     
        cost: Number(Number(addTransactionData.cost)),        
        orderPrice: Number(Number(addTransactionData.orderPrice)),
      };

      const newTransaction = await dispatch(
        addTransactionToAsset({
          canvasId: selectedCanvas.id,
          assetName: selectedAsset.assetName,
          ticker: selectedAsset.ticker,
          transactionData: transactionPayload,
        })
      ).unwrap();
      
      setShowAddTransactionForm(false);
      setAddTransactionData({
        trade: "",
        units: "",
        cost: "",
        orderPrice: "",
        paymentAccountId: "",
        currency: "",
        date: new Date().toISOString().split('T')[0],
        notes: "",
      });
      
      const transactionToAdd = {
        ...transactionPayload,
        _id: newTransaction._id || newTransaction.id || Date.now().toString(),
        createdAt: newTransaction.createdAt || new Date().toISOString(),
        date: transactionPayload.date || new Date().toISOString().split('T')[0]
      };
      
      const updatedTransactions = [...assetTransactions, transactionToAdd].sort((a, b) => 
        new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
      );
      setAssetTransactions(updatedTransactions);
      
      const totalUnits = updatedTransactions.reduce((sum, t) => {
        const isBuy = t.trade?.toLowerCase() === 'buy';
        return sum + (isBuy ? Number(t.units || 0) : -Number(t.units || 0));
      }, 0);
      
      const totalCost = updatedTransactions.reduce((sum, t) => {
        const isBuy = t.trade?.toLowerCase() === 'buy';
        return sum + (isBuy ? Number(t.cost || 0) : -Number(t.cost || 0));
      }, 0);

      setSelectedAsset(prev => {
        const weightedAverageOrderPrice = totalUnits > 0 ? totalCost / totalUnits : 0;
        const lastClosePrice = prev.lastClosePrice || weightedAverageOrderPrice;
        const currentValue = totalUnits * lastClosePrice;
        const profitLoss = currentValue - totalCost;
        const profitLossPercentage = totalCost > 0 ? ((profitLoss / totalCost) * 100) : 0;
        return {
          ...prev,
          transactions: updatedTransactions,
          totalUnits,
          totalCost,
          averageOrderPrice: Number(weightedAverageOrderPrice.toFixed(4)),
          currentPrice: Number(lastClosePrice.toFixed(2)),
          currentValue,
          profitLoss,
          profitLossPercentage
        };
      });
      
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
      
      toast.success("Transaction Added Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle inline editing
  const handleEditTransaction = (transaction) => {
    setEditTransactionData({ ...transaction });
    setShowEditTransactionModal(true);
  };

  const handleEditTransactionChange = (field, value) => {
    setEditTransactionData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id || !editTransactionData) {
      toast.error("Invalid data");
      return;
    }
    setIsUpdating(true);
    try {
      const updatePayload = {
        units: Number(Number(editTransactionData.units)), 
        cost: Number(Number(editTransactionData.cost)),  
        orderPrice: Number(Number(editTransactionData.orderPrice)), 
        notes: editTransactionData.notes,
      };

      await dispatch(
        updateTransaction({
          canvasId: selectedCanvas.id,
          transactionId: editTransactionData._id,
          transactionData: updatePayload,
        })
      ).unwrap();

      setShowEditTransactionModal(false);
      
      const updatedTransactions = assetTransactions.map(transaction => 
        transaction._id === editTransactionData._id 
          ? { 
            ...transaction,
            units: updatePayload.units,
            cost: updatePayload.cost,
            orderPrice: updatePayload.orderPrice,
            notes: updatePayload.notes
          }
        : transaction
      );
      setAssetTransactions(updatedTransactions);
      
      const totalUnits = updatedTransactions.reduce((sum, t) => {
        const isBuy = t.trade?.toLowerCase() === 'buy';
        return sum + (isBuy ? Number(t.units || 0) : -Number(t.units || 0));
      }, 0);
      
      const totalCost = updatedTransactions.reduce((sum, t) => {
        const isBuy = t.trade?.toLowerCase() === 'buy';
        return sum + (isBuy ? Number(t.cost || 0) : -Number(t.cost || 0));
      }, 0);

      setSelectedAsset(prev => {
        const weightedAverageOrderPrice = totalUnits > 0 ? totalCost / totalUnits : 0;
        const lastClosePrice = prev.lastClosePrice || weightedAverageOrderPrice;
        const currentValue = totalUnits * lastClosePrice;
        const profitLoss = currentValue - totalCost;
        const profitLossPercentage = totalCost > 0 ? ((profitLoss / totalCost) * 100) : 0;
        return {
          ...prev,
          transactions: updatedTransactions,
          totalUnits,
          totalCost,
          averageOrderPrice: Number(weightedAverageOrderPrice.toFixed(4)),
          currentPrice: Number(lastClosePrice.toFixed(2)),
          currentValue,
          profitLoss,
          profitLossPercentage
        };
      });
      
      setEditTransactionData(null);
      
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
      
      toast.success("Transaction Updated Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEditTransaction = () => {
    setShowEditTransactionModal(false);
    setEditTransactionData(null);
  };

  const handleDeleteTransaction = async () => {
    if (!selectedTransaction || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Transaction");
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteInvestment({
          canvasId: selectedCanvas.id,
          investmentId: selectedTransaction._id,
        })
      ).unwrap();
      
      setShowDeleteConfirm(false);
      
      const updatedTransactions = assetTransactions.filter(t => t._id !== selectedTransaction._id);
      setAssetTransactions(updatedTransactions);
      
      const totalUnits = updatedTransactions.reduce((sum, t) => {
        const isBuy = t.trade?.toLowerCase() === 'buy';
        return sum + (isBuy ? Number(t.units || 0) : -Number(t.units || 0));
      }, 0);
      
      const totalCost = updatedTransactions.reduce((sum, t) => {
        const isBuy = t.trade?.toLowerCase() === 'buy';
        return sum + (isBuy ? Number(t.cost || 0) : -Number(t.cost || 0));
      }, 0);

      if (updatedTransactions.length > 0) {
        setSelectedAsset(prev => {
          const lastClosePrice = prev.lastClosePrice || prev.orderPrice;
          const currentValue = totalUnits * lastClosePrice;
          const profitLoss = currentValue - totalCost;
          const profitLossPercentage = totalCost > 0 ? ((profitLoss / totalCost) * 100) : 0;
          return {
            ...prev,
            transactions: updatedTransactions,
            totalUnits,
            totalCost,
            currentValue,
            profitLoss,
            profitLossPercentage
          };
        });
      } else {
        // If no transactions left, go back to portfolio
        handleBackToPortfolio();
      }
      
      setSelectedTransaction(null);
      
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
      
      toast.success("Transaction Deleted Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAsset = async () => {
    if (!selectedAsset || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Asset");
      return;
    }
    setIsDeleting(true);
    try {
  
      await dispatch(
        deleteAssetWithTransactions({
          canvasId: selectedCanvas.id,
          assetName: selectedAsset.assetName,
          ticker: selectedAsset.ticker,
        })
      ).unwrap();
      
      setShowDeleteAssetConfirm(false);
      
      handleBackToPortfolio();
      
      // Refresh data
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
      
      toast.success("Asset and all transactions deleted successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const sampleAccountName = accounts && accounts.length > 0 ? accounts[0].name : 'Investment Account';
      const sampleCurrency = selectedCanvas?.currency || 'USD';

      const templateData = [
        {
          assetName: 'Apple Inc.',
          ticker: 'AAPL',
          type: 'Stock',
          trade: 'buy',
          units: '10',
          cost: '1505.00',
          orderPrice: '150.00',
          currency: 'USD',
          date: '2024-01-15',
          paymentAccountId: sampleAccountName,
          notes: 'Initial purchase'
        },
        {
          assetName: 'Vanguard S&P 500 ETF',
          ticker: 'VOO',
          type: 'ETF',
          trade: 'buy',
          units: '5',
          cost: '2009.95',
          orderPrice: '400.00',
          currency: 'USD',
          date: '2024-02-01',
          paymentAccountId: sampleAccountName,
          notes: 'Monthly investment'
        },
        {
          assetName: 'Bitcoin',
          ticker: 'BTC-USD',
          type: 'Crypto',
          trade: 'buy',
          units: '0.1',
          cost: '4525.00',
          orderPrice: '45000.00',
          currency: 'USD',
          date: '2024-02-15',
          paymentAccountId: sampleAccountName,
          notes: 'Crypto diversification'
        },
        {
          assetName: 'Tesla Inc.',
          ticker: 'TSLA',
          type: 'Stock',
          trade: 'buy',
          units: '20',
          cost: '5025.00',
          orderPrice: '250.00',
          currency: 'USD',
          date: '2024-03-01',
          paymentAccountId: sampleAccountName,
          notes: 'Growth stock purchase'
        }
      ];

      const csvContent = [
        'assetName,ticker,type,trade,units,cost,orderPrice,currency,date,paymentAccountId,notes',
        ...templateData.map(row =>
          `"${row.assetName}","${row.ticker}","${row.type}","${row.trade}",${row.units},${row.cost},${row.orderPrice},"${row.currency}","${row.date}","${row.paymentAccountId}","${row.notes}"`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `investments_template_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setShowInstructions(false)
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error("Invalid Canvas ID or file");
      return;
    }
    try {
      await dispatch(uploadInvestments({ canvasId: selectedCanvas.id, file })).unwrap();
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
      toast.success("Investments Imported Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id, limit:1000 }));
      dispatch(investmentOverview({ canvasId: selectedCanvas.id }));
    }
  }, [dispatch, selectedCanvas?.id]);

  // Handle Edit Investment Asset
  const handleEditAsset = (asset) => {
    const firstTransaction = asset.transactions && asset.transactions[0];
    if (!firstTransaction) {
      toast.error("Cannot edit asset without transactions");
      return;
    }
    
    setEditAssetData({
      assetName: asset.assetName,
      ticker: asset.ticker,
      type: asset.type,
      currency: asset.currency,
      exchange: firstTransaction.exchange || '',
      trade: firstTransaction.trade || 'buy',
      units: firstTransaction.units || '',
      cost: firstTransaction.cost || '',
      orderPrice: firstTransaction.orderPrice || '',
      paymentAccountId: firstTransaction.paymentAccountId || '',
      date: firstTransaction.date ? new Date(firstTransaction.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      notes: firstTransaction.notes || '',
      _id: firstTransaction._id,
      originalAssetName: asset.assetName,
      originalTicker: asset.ticker,
      originalType: asset.type,
      originalCurrency: asset.currency,
      allTransactionIds: asset.transactions.map(t => t._id),
    });
    setEditingAsset(asset);
    setShowEditAssetModal(true);
  };

  const handleEditAssetChange = (field, value) => {
    setEditAssetData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditAssetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id || !editingAsset || !editAssetData?._id) {
      toast.error("Invalid asset data");
      return;
    }
    
    setIsUpdating(true);
    try {
        const assetFields = {
          assetName: editAssetData.assetName,
          ticker: editAssetData.ticker,
          type: editAssetData.type,
          currency: editAssetData.currency,
          exchange: editAssetData.exchange,
        };

        const transactionFields = {
          units: Number(editAssetData.units),
          cost: Number(editAssetData.cost),
          orderPrice: Number(editAssetData.orderPrice),
          paymentAccountId: editAssetData.paymentAccountId,
          date: editAssetData.date,
          notes: editAssetData.notes,
        };

        await dispatch(updateAsset({
          canvasId: selectedCanvas.id,
          assetId: editAssetData._id,
          assetName: editAssetData.originalAssetName,
          ticker: editAssetData.originalTicker,
          assetData: assetFields,
        })).unwrap();
 
        await dispatch(updateTransaction({
          canvasId: selectedCanvas.id,
          transactionId: editAssetData._id,
          transactionData: transactionFields,
        })).unwrap();

        // Refresh data
        await dispatch(fetchInvestments({ canvasId: selectedCanvas.id, page: 1, limit: 10000 }));
        await dispatch(investmentOverview({ canvasId: selectedCanvas.id }));

        setShowEditAssetModal(false);
        setEditingAsset(null);
        setEditAssetData(null);

        toast.success("Asset Updated Successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update asset");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEditAsset = () => {
    setShowEditAssetModal(false);
    setEditingAsset(null);
    setEditAssetData(null);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header with Navigation */}
       <div className="mb-4 md:mb-6">
        <div className="flex justify-center items-center gap-4">
          {currentView === 'transactions' && (
            <button
              onClick={handleBackToPortfolio}
              className="flex items-center gap-2 text-btn-primary hover:text-btn-primary-hover transition-colors"
            >
              <FiArrowLeft className="text-lg" />
              <span></span>
            </button>
          )}
          <h1 className="text-xl md:text-2xl font-bold text-center">
            {currentView === 'portfolio'
              ? 'Investment'
              : `${selectedAsset?.assetName} - Transactions`
            }
          </h1>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        {currentView === 'portfolio' ? (
          <>
            {/* Portfolio Summary Cards */}
            <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
              <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">

                <SummaryCard
                  title="Total Investments"
                  value={overview?.totalInvestmentCost !== undefined ? formatCurrency(overview.totalInvestmentCost) : "Auto calculated"}
                  icon={<FaMoneyBillTrendUp className="w-5 h-5" />}
                  valueColor="text-btn-primary"
                  iconBg="bg-blue-100"
                  iconColor="text-btn-primary"
                  formatValue={val => formatCurrency(overview?.totalInvestmentCost)}
                />
                <SummaryCard
                  title="Total Portfolio Value"
                  value={overview?.totalPortfolioValue !== undefined ? formatCurrency(overview.totalPortfolioValue) : "Auto calculated"}
                  icon={<FiPieChart className="w-5 h-5" />}
                  valueColor="text-green-700"
                  iconBg="bg-green-100"
                  iconColor="text-green-600"
                  formatValue={val => formatCurrency(overview?.totalPortfolioValue)}
                />
                <SummaryCard
                  title="P/L %"
                  value={
                    overview?.profitLossPercentage !== undefined
                      ? formatPercentage(overview.profitLossPercentage)
                      : "Auto calculated"
                  }
                  icon={
                    overview?.profitLossPercentage >= 0 ? (
                      <FiTrendingUp className="w-5 h-5" />
                    ) : (
                      <FiTrendingDown className="w-5 h-5" />
                    )
                  }
                  valueColor={
                    overview?.profitLossPercentage >= 0 ? "text-green-700" : "text-red-700"
                  }
                  iconBg={
                    overview?.profitLossPercentage >= 0 ? "bg-green-100" : "bg-red-100"
                  }
                  iconColor={
                    overview?.profitLossPercentage >= 0 ? "text-green-600" : "text-red-600"
                  }
                  formatValue={(val) =>
                    typeof val === "number" ? `${val.toFixed(2)}%` : val
                  }
                />

              </div>

              {/* Add Investment Button */}
              <AddOptionsButton
                showOptions={showAddOptions}
                setShowOptions={setShowAddOptions}
                addOptionsRef={addOptionsRef}
                onAddManual={() => setShowAddForm(true)}
                fileInputRef={fileInputRef}
                handleFileChange={handleFileChange}
                onShowInstructions={() => setShowInstructions(true)}
                buttonLabel="Add Investment"
                buttonTitle="Add Investment"
                manualLabel="Add Manually"
                FileUploadButtonComponent={FileUploadButton}
              />
            </div>

            {/* Portfolio Assets Table*/}
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <TableHeader columns={portfolioColumns} />
                    <TableBody
                      loading={loading}
                      data={paginatedPortfolioAssets}
                      columnsCount={13}
                      emptyMessage="No Investments Found."
                      loadingMessage="Loading Investments..."
                      renderRow={asset => (
                        <tr 
                          key={`${asset.assetName}-${asset.ticker}`} 
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td 
                            className="px-3 py-4 whitespace-nowrap text-sm font-medium text-btn-primary hover:text-btn-primary-hover cursor-pointer"
                            onClick={() => handleAssetClick(asset)}
                            title="Click to view transactions"
                          >
                            {asset.assetName}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {asset.ticker}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {asset.type}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {asset.currency || '—'}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatUnits(asset.totalUnits)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(asset.totalCost, asset.currency)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {asset.portfolioPercentage.toFixed(1)}%
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(asset.averageOrderPrice, asset.currency)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(asset.currentPrice, asset.currency)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {asset?.lastClosePriceDate ? new Date(asset?.lastClosePriceDate).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(asset.currentValue, asset.currency)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm">
                            <div className={`font-medium ${asset.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <div className="flex items-center gap-1">
                                {asset.profitLoss >= 0 ? (
                                  <FiTrendingUp className="w-4 h-4" />
                                ) : (
                                  <FiTrendingDown className="w-4 h-4" />
                                )}
                                <span>{formatCurrency(asset.profitLoss, asset.currency)}</span>
                              </div>
                              <div className="text-xs">
                                ({formatPercentage(asset.profitLossPercentage)})
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  size="small"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditAsset(asset);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(37, 99, 235, 0.1)'
                                    }
                                  }}
                                >
                                  <FiEdit2 size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete" arrow>
                                <IconButton
                                  size="small"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAsset(asset);
                                    setShowDeleteAssetConfirm(true);
                                  }}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                    }
                                  }}
                                >
                                  <FiTrash2 size={16} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      )}
                    />
                  </table>
                </div>
                
                {/* Portfolio Pagination */}
                <Pagination
                  pagination={portfolioPagination}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  PAGE_SIZE_OPTIONS={[5, 10, 15, 20]}
                  label="Assets per page"
                  totalLabel={`Total: ${portfolioPagination?.total || 0} assets`}
                />
              </div>
            </div>
          </>
        ) : (
          <>
      
            {/* Asset Details */}
            <div className="flex flex-wrap md:justify-between gap-4 md:gap-8 mb-6">
              <div className="flex flex-wrap gap-4 md:gap-8 wi-full md:w-auto">

                <SummaryCard
                  title="Total Units"
                  value={formatUnits(selectedAsset?.totalUnits)}
                  icon={<FaCoins className="w-5 h-5" />}
                  valueColor="text-btn-primary"
                  iconBg="bg-blue-100"
                  iconColor="text-btn-primary"
                  formatValue={val => val}
                />
                <SummaryCard
                  title="Total Invested"
                  value={formatCurrency(selectedAsset?.totalCost, null, selectedAsset?.currency)}
                  icon={<FaMoneyBillTrendUp className="w-5 h-5" />} 
                  valueColor="text-green-700"
                  iconBg="bg-green-100"
                  iconColor="text-green-600"
                  formatValue={val => val}
                />
                <SummaryCard
                  title="Current Value"
                  value={formatCurrency(selectedAsset?.currentValue, null, selectedAsset?.currency)}
                  icon={<MdShowChart className="w-5 h-5" />}
                  valueColor="text-green-700"
                  iconBg="bg-green-100"
                  iconColor="text-green-600"
                  formatValue={val => val}
                />
                <SummaryCard
                  title="P/L"
                  value={formatCurrency(selectedAsset?.profitLoss, null, selectedAsset?.currency)}
                  icon={
                    selectedAsset?.profitLoss >= 0
                      ? <FiTrendingUp className="w-5 h-5" />
                      : <FiTrendingDown className="w-5 h-5" />
                  }
                  valueColor={selectedAsset?.profitLoss >= 0 ? "text-green-700" : "text-red-700"}
                  iconBg={selectedAsset?.profitLoss >= 0 ? "bg-green-100" : "bg-red-100"}
                  iconColor={selectedAsset?.profitLoss >= 0 ? "text-green-600" : "text-red-600"}
                  formatValue={val => val}
                />
      
              </div>
              {/* Add Transaction Button */}
              <div className="relative">
                <button
                  onClick={handleAddTransaction}
                  className="flex items-center gap-2 bg-btn-primary text-white px-4 py-3 rounded-full shadow-lg hover:bg-btn-primary-hover transition-colors"
                  title="Add Transaction"
                >
                  <FiPlus className="text-sm" />
                  <span className="hidden sm:inline">Add Transaction</span>
                </button>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <TableHeader columns={transactionColumns} />
                    <TableBody
                      loading={false}
                      data={paginatedTransactions}
                      columnsCount={9}
                      emptyMessage="No Transactions Found."
                      loadingMessage="Loading Transactions..."
                      renderRow={transaction => (
                        <tr key={transaction._id}>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(transaction.date || transaction.createdAt)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              transaction.trade?.toLowerCase() === 'buy' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {transaction.trade?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {transaction.currency || selectedAsset?.currency || '—'}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatUnits(transaction.units)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(transaction.orderPrice, null, selectedAsset?.currency)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(transaction.cost, null, selectedAsset?.currency)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {accounts.find((a) => a._id === transaction.paymentAccountId)?.name || "—"}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.notes || "—"}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm">
                            <ActionButtons
                              onEdit={() => handleEditTransaction(transaction)}
                              onDelete={() => {
                                setSelectedTransaction(transaction);
                                setShowDeleteConfirm(true);
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    />
                  </table>
                </div>
                
                {/* Transaction Pagination */}
                <Pagination
                  pagination={transactionPagination}
                  pageSize={transactionPageSize}
                  setPageSize={setTransactionPageSize}
                  currentPage={transactionPage}
                  setCurrentPage={setTransactionPage}
                  PAGE_SIZE_OPTIONS={[5, 10, 15, 20]}
                  label="Transactions per page"
                  totalLabel={`Total: ${transactionPagination?.total || 0} transactions`}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Investment Modal */}
      <EditModal
        show={showAddForm}
        title="Add New Investment"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleCreateInvestment}
        loading={isAdding}
        submitLabel="Create Investment"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Asset Name"
            type="text"
            value={addInvestmentData.assetName}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, assetName: e.target.value })}
            required
          />
          <FormInput
            label="Ticker Symbol"
            type="text"
            value={addInvestmentData.ticker}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, ticker: e.target.value.toUpperCase() })}
            required
            placeholder="e.g. AAPL, RIO.L, BTC-USD"
            inputProps={{
              title: "Enter the official stock/asset ticker symbol (e.g. AAPL for Apple, NVDA for NVIDIA). This is used to fetch live market prices. Make sure it is correct."
            }}
          />
          <FormSelect
            label="Type"
            value={addInvestmentData.type}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, type: e.target.value })}
            required
          >
            <option value="">Select Type</option>
            {investmentTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Trade Type"
            value={addInvestmentData.trade}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, trade: e.target.value })}
            required
          >
            <option value="">Select Trade</option>
            {tradeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Units"
            type="number"
            value={addInvestmentData.units}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, units: e.target.value })}
            min="0"
            step="0.000000000000000001"
            placeholder="e.g., 0.00123456 for crypto"
            required
          />
          <FormInput
            label="Total Cost"
            type="number"
            value={addInvestmentData.cost}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, cost: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Price per Unit"
            type="number"
            value={addInvestmentData.orderPrice}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, orderPrice: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Transaction Date"
            type="date"
            value={addInvestmentData.date}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, date: e.target.value })}
            required
          />
          <FormSelect
            label="Payment Account"
            value={addInvestmentData.paymentAccountId}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, paymentAccountId: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Currency"
            value={addInvestmentData.currency}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, currency: e.target.value })}
            required
          >
            <option value="">Select Currency</option>
            {currencyOptions.map(curr=>(
              <option key={curr.code} value={curr.code}>
                {curr.name}-{curr.symbol}
              </option>
            ))}
          </FormSelect>
          <FormInput
            label="Notes (Optional)"
            type="text"
            value={addInvestmentData.notes}
            onChange={e => setAddInvestmentData({ ...addInvestmentData, notes: e.target.value })}
            placeholder="Transaction notes..."
          />
        </div>
      </EditModal>

      {/* Add Transaction Modal */}
      <EditModal
        show={showAddTransactionForm}
        title={`Add Transaction - ${selectedAsset?.assetName}`}
        onCancel={() => setShowAddTransactionForm(false)}
        onSubmit={handleCreateTransaction}
        loading={isAdding}
        submitLabel="Add Transaction"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormSelect
            label="Trade Type"
            value={addTransactionData.trade}
            onChange={e => setAddTransactionData({ ...addTransactionData, trade: e.target.value })}
            required
          >
            <option value="">Select Trade</option>
            {tradeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Units"
            type="number"
            value={addTransactionData.units}
            onChange={e => setAddTransactionData({ ...addTransactionData, units: e.target.value })}
            min="0"
            step="0.000000000000000001"
            placeholder="e.g., 0.00123456 for crypto"
            required
          />
          <FormInput
            label="Total Cost"
            type="number"
            value={addTransactionData.cost}
            onChange={e => setAddTransactionData({ ...addTransactionData, cost: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Price per Unit"
            type="number"
            value={addTransactionData.orderPrice}
            onChange={e => setAddTransactionData({ ...addTransactionData, orderPrice: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Transaction Date"
            type="date"
            value={addTransactionData.date}
            onChange={e => setAddTransactionData({ ...addTransactionData, date: e.target.value })}
            required
          />
          <FormSelect
            label="Payment Account"
            value={addTransactionData.paymentAccountId}
            onChange={e => setAddTransactionData({ ...addTransactionData, paymentAccountId: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Currency"
            value={addTransactionData.currency}
            onChange={e => setAddTransactionData({ ...addTransactionData, currency: e.target.value })}
            required
          >
            <option value="">Select Currency</option>
             {currencyOptions.map(curr=>(
              <option key={curr.code} value={curr.code}>
                {curr.name}-{curr.symbol}
              </option>
            ))}
          </FormSelect>
          <FormInput
            label="Notes (Optional)"
            type="text"
            value={addTransactionData.notes}
            onChange={e => setAddTransactionData({ ...addTransactionData, notes: e.target.value })}
            placeholder="Transaction notes..."
          />
        </div>
      </EditModal>

      {/* Edit Transaction Modal */}
      <EditModal
        show={showEditTransactionModal}
        title="Edit Transaction"
        onCancel={handleCancelEditTransaction}
        onSubmit={handleEditTransactionSubmit}
        loading={isUpdating}
        submitLabel="Save Changes"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Units"
            type="number"
            value={editTransactionData?.units || ""}
            onChange={e => handleEditTransactionChange("units", e.target.value)}
            min="0"
            step="0.000000000000000001"
            placeholder="e.g., 0.00123456 for crypto"
            required
          />
          <FormInput
            label="Total Cost"
            type="number"
            value={editTransactionData?.cost || ""}
            onChange={e => handleEditTransactionChange("cost", e.target.value)}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Price per Unit"
            type="number"
            value={editTransactionData?.orderPrice || ""}
            onChange={e => handleEditTransactionChange("orderPrice", e.target.value)}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Notes (Optional)"
            type="text"
            value={editTransactionData?.notes || ""}
            onChange={e => handleEditTransactionChange("notes", e.target.value)}
            placeholder="Transaction notes..."
          />
        </div>
      </EditModal>

      {/* Delete Transaction Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedTransaction(null);
        }}
        onConfirm={handleDeleteTransaction}
        loading={isDeleting}
      />

      {/* Delete Asset Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteAssetConfirm}
        title="Delete Asset"
        message={`Are you sure you want to delete "${selectedAsset?.assetName}" and all its transactions? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteAssetConfirm(false);
          setSelectedAsset(null);
        }}
        onConfirm={handleDeleteAsset}
        loading={isDeleting}
      />

      {/* Instructions Modal */}
      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="investments"
        onDownloadTemplate={handleDownloadTemplate}
      />

      {/* Edit Asset Modal */}
      <EditModal
        show={showEditAssetModal}
        title="Edit Asset"
        onCancel={handleCancelEditAsset}
        onSubmit={handleEditAssetSubmit}
        loading={isUpdating}
        submitLabel="Save Changes"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Asset Name"
            type="text"
            value={editAssetData?.assetName || ""}
            onChange={e => handleEditAssetChange("assetName", e.target.value)}
            required
            placeholder="e.g., Apple Inc., Bitcoin"
          />
          <FormInput
            label="Ticker Symbol"
            type="text"
            value={editAssetData?.ticker || ""}
            onChange={e => handleEditAssetChange("ticker", e.target.value.toUpperCase())}
            required
            placeholder="e.g. AAPL, RIO.L, BTC-USD"
          />
          <FormSelect
            label="Type"
            value={editAssetData?.type || ""}
            onChange={e => handleEditAssetChange("type", e.target.value)}
            required
          >
            <option value="">Select Type</option>
            {investmentTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Trade Type"
            value={editAssetData?.trade || ""}
            onChange={e => handleEditAssetChange("trade", e.target.value)}
            required
          >
            <option value="">Select Trade</option>
            {tradeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Units"
            type="number"
            value={editAssetData?.units || ""}
            onChange={e => handleEditAssetChange("units", e.target.value)}
            min="0"
            step="0.000000000000000001"
            placeholder="e.g., 0.00123456 for crypto"
            required
          />
          <FormInput
            label="Total Cost"
            type="number"
            value={editAssetData?.cost || ""}
            onChange={e => handleEditAssetChange("cost", e.target.value)}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Price per Unit"
            type="number"
            value={editAssetData?.orderPrice || ""}
            onChange={e => handleEditAssetChange("orderPrice", e.target.value)}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Transaction Date"
            type="date"
            value={editAssetData?.date || ""}
            onChange={e => handleEditAssetChange("date", e.target.value)}
            required
          />
          <FormSelect
            label="Payment Account"
            value={editAssetData?.paymentAccountId || ""}
            onChange={e => handleEditAssetChange("paymentAccountId", e.target.value)}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Currency"
            value={editAssetData?.currency || ""}
            onChange={e => handleEditAssetChange("currency", e.target.value)}
            required
          >
            <option value="">Select Currency</option>
            {currencyOptions.map(curr => (
              <option key={curr.code} value={curr.code}>
                {curr.name} - {curr.symbol}
              </option>
            ))}
          </FormSelect>
          <FormInput
            label="Notes (Optional)"
            type="text"
            value={editAssetData?.notes || ""}
            onChange={e => handleEditAssetChange("notes", e.target.value)}
            placeholder="Asset notes..."
          />
        </div>
      </EditModal>
    </div>
  );
};

export default InvestmentContent;
